import React, { ReactNode, useId, useRef, useState } from "react";
import { TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import SnackBar, { SnackTypes } from "../SnackBar/SnackBar";
import FileUploadIcon from '@mui/icons-material/FileUpload';

export interface UploadableImage {
    remoteUrl?: string,
    localUrl?: string,
    file?: File
  }

interface UploadImagesSectionDraggableProps {
  editMode: boolean;
  maxLimit: number
  images: UploadableImage[];
  setImages: (images: UploadableImage[]) => void;
}

type DragAndDropProps<T> = {
  items: T[];
  render(item: T, index: number): ReactNode;
  onDragEnd(newItems: T[]): void;
  editMode: Boolean;
};

function DragAndDrop<T>({ items, render, onDragEnd, editMode }: DragAndDropProps<T>) {
  const [temporaryItems, setTemporaryItems] = useState<T[]>();
  const [draggedItem, setDraggedItem] = useState<T>();
  const draggedItemRef = useRef<T>();
  const temporaryItemsRef = useRef<T[]>();

  const renderedItems = temporaryItems || items;

  const finishDrag = () => {
    if (!draggedItemRef.current) return;
    if (temporaryItemsRef.current) onDragEnd(temporaryItemsRef.current);
    draggedItemRef.current = undefined;
    temporaryItemsRef.current = undefined;
    setDraggedItem(undefined);
    setTemporaryItems(undefined);
  };

  return (
    <>
      {renderedItems.map((item, index) => (
        <div
          key={(item as any).remoteUrl ?? (item as any).localUrl ?? index}
          style={{ cursor: "grab", ...(item === draggedItem ? { opacity: "0.4", border: "2px dashed grey" } : {}) }}
          draggable={editMode ? true : false}
          onDragStart={() => {
            if(!editMode) return
            draggedItemRef.current = item;
            setDraggedItem(item)
          }}
          onDragOver={(e) => {
            if (!editMode) return 
            e.preventDefault();
            const activeItem = draggedItemRef.current;
            if (!activeItem || activeItem === item) {
              return;
            }
            const currentItems = temporaryItemsRef.current || items;
            const currentIndex = currentItems.indexOf(activeItem);
            const targetIndex = currentItems.indexOf(item);

            if (currentIndex !== -1 && targetIndex !== -1) {
              const newItems = [...currentItems];
              newItems.splice(currentIndex, 1);
              newItems.splice(targetIndex, 0, activeItem);
              temporaryItemsRef.current = newItems;
              setTemporaryItems(newItems);
            }
          }}
          onDrop={(event) => {
            if (!editMode) return;
            event.preventDefault();
            finishDrag();
          }}
          onDragEnd={finishDrag}
        >
          {render(item, index)}
        </div>
      ))}
    </>
  );
}

const UploadImagesSectionDraggable: React.FC<UploadImagesSectionDraggableProps> = ({ editMode, images, setImages, maxLimit = 50 }) => {
  const fileInputId = useId();

  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  const handleFilesAdded = (event: any) => {
    if (event.target.files) {
      if (images.length + event.target.files.length > maxLimit) {
        const snackMsg = "A maximum of " + maxLimit + " images can be uploaded"
        showSnackbar(snackMsg, SnackTypes.error)
        return;
      }

      let imageList: UploadableImage[] = [...images];
      Array.from(event.target.files).forEach((element: any) => {
        imageList.push({
          localUrl: URL.createObjectURL(element),
          file: element
        });
      });
      setImages(imageList);
    }
  }
  
  const handleFileRemoved = (index: number) => {
    setImages([...images].filter((_, idx) => idx !== index))
  }

  return (
    <div style={{ display: "flex", textAlign: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
      <DragAndDrop 
        items={images} 
        onDragEnd={(newImages: UploadableImage[]) => {
            setImages(newImages);
        } } 
        render={renderImage} 
        editMode={editMode} 
      />
      {editMode && (
        <>
          <label htmlFor={fileInputId}>
            <Box
              className="portfolio-image upload-icon"
              height={{ xs: "100px", md: "130px" }}
              width={{ xs: "100px", md: "130px" }}
              display={images.length >= maxLimit ? "none" : "block"}
              sx={{
                backgroundColor: "#F5F5F5",
                border: "2px dashed #C4C4C4",
                borderRadius: "10px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#E0E0E0"
                }
              }}
            >
              <FileUploadIcon></FileUploadIcon>
              <Typography>Upload</Typography>
            </Box>
          </label>
          <TextField
            id={fileInputId}
            type="file"
            sx={{ display: "none" }}
            onChange={handleFilesAdded}
            inputProps={{ multiple: true, accept: 'image/png, image/jpg, image/jpeg' }}
          />
          <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
        </>
      )}
    </div>
  );

  function renderImage(image: UploadableImage, index: number): ReactNode {
    return (
      <div className="image-upload-container">
        <div
          key={index}
          className="image-box"
          style={{ cursor: "grab" }}
        >
          <Box
            component="img"
            className="portfolio-image"
            src={image.remoteUrl ?? image.localUrl}
            height={{ xs: "100px", md: "150px" }}
            width={{ xs: "100px", md: "150px" }}
          />
          <div>
            {editMode && (
              <div className="cross-button">
                <CancelRoundedIcon
                  className="delete-icon"
                  fontSize="large"
                  color="error"
                  onClick={() => {
                    handleFileRemoved(index);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default UploadImagesSectionDraggable;
